import { Button, PocketRow } from "@/components/ui";
import { useCallback, useEffect, useState } from "react";
import type { CatalogSkill } from "@/api/agents";
import * as agentsApi from "@/api/agents";
import { Icons } from "@/components/ui/icons";
import { TextInput } from "@/components/ui/text-input";
import { useT } from "@/i18n";
import { settingsSkillsMessages } from "@/i18n/messages/settings-skills";
import { cn } from "@/lib/cn";

interface SkillCatalogBrowserProps {
  existingSlugs: Set<string>;
  onImport: (entry: CatalogSkill) => Promise<void>;
  onClose?: () => void;
  expanded?: boolean;
  page?: boolean;
}

export function SkillCatalogBrowser({
  existingSlugs,
  onImport,
  onClose,
  expanded = false,
  page = false,
}: SkillCatalogBrowserProps) {
  const t = useT(settingsSkillsMessages);
  const [catalog, setCatalog] = useState<CatalogSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [search, setSearch] = useState("");
  const [importing, setImporting] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    agentsApi
      .listSkillCatalog()
      .then(
        (entries) => {
          if (active) setCatalog(entries);
        },
        () => {
          if (active) setError(true);
        },
      )
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [reload]);

  const handleImport = useCallback(
    async (entry: CatalogSkill) => {
      setImporting(entry.slug);
      setError(false);
      try {
        await onImport(entry);
        setCatalog((current) =>
          current.map((item) =>
            item.slug === entry.slug ? { ...item, imported: true } : item,
          ),
        );
      } catch {
        setError(true);
      } finally {
        setImporting(null);
      }
    },
    [onImport],
  );

  const filtered = catalog.filter(
    (entry) =>
      !existingSlugs.has(entry.slug) &&
      !entry.imported &&
      (search === "" ||
        [entry.name, entry.description, entry.category].some((value) =>
          value.toLowerCase().includes(search.toLowerCase()),
        )),
  );

  return (
    <div
      className={cn(
        "flex flex-col gap-2",
        page
          ? "px-2"
          : "rounded-panel border border-glass-stroke bg-glass-fill p-appkit-sm",
      )}
      data-testid="skill-catalog-browser"
    >
      <div className="flex items-center justify-between">
        <span className="text-appkit-mini font-medium text-primary">
          {t("skillCatalog")}
        </span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="flex h-5 w-5 items-center justify-center rounded-control-sm text-muted hover:text-primary"
            data-testid="skill-catalog-close"
            aria-label={t("closeSkillCatalog")}
          >
            <Icons.X className="h-3 w-3" />
          </button>
        )}
      </div>
      <TextInput
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        shape="capsule"
        aria-label={t("searchCatalogSkills")}
        placeholder={t("searchSkillsPlaceholder")}
        data-testid="skill-catalog-search"
      />
      {error && (
        <div
          role="alert"
          className="flex items-center gap-2 text-appkit-mini text-danger"
          data-testid="skill-catalog-error"
        >
          <span>{t("catalogError")}</span>
          <button
            type="button"
            className="rounded-control-sm px-2 py-1 text-accent hover:bg-[color-mix(in_srgb,var(--color-accent)_10%,transparent)]"
            onClick={() => {
              setError(false);
              setLoading(true);
              setReload((value) => value + 1);
            }}
            data-testid="skill-catalog-retry"
          >
            {t("retry")}
          </button>
        </div>
      )}
      <div
        className={cn(
          "scrollbar-none flex flex-col gap-1 overflow-y-auto",
          !page && (expanded ? "max-h-80" : "max-h-48"),
        )}
      >
        {loading && (
          <p className="py-2 text-center text-appkit-mini text-muted">
            {t("loadingCatalog")}
          </p>
        )}
        {!loading && !error && filtered.length === 0 && (
          <p className="py-2 text-center text-appkit-mini text-muted">
            {search ? t("noMatchingSkills") : t("allSkillsAdded")}
          </p>
        )}
        {filtered.map((entry) =>
          page ? (
            <PocketRow
              key={entry.slug}
              testId={`skill-catalog-${entry.slug}`}
              title={entry.name}
              // A catalog's descriptions: two lines each, where settings wrap.
              secondLine={
                <span className="line-clamp-2">{entry.description}</span>
              }
              trailing={
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={importing === entry.slug}
                  data-testid={`skill-catalog-import-${entry.slug}`}
                  onClick={() => void handleImport(entry)}
                >
                  {importing === entry.slug ? t("adding") : t("add")}
                </Button>
              }
            />
          ) : (
            <div
              key={entry.slug}
              className="flex items-center gap-2 rounded-control border border-glass-stroke-subtle px-2.5 py-2"
              data-testid={`skill-catalog-${entry.slug}`}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex items-center gap-1.5">
                  <span className="text-appkit-mini font-medium text-primary">
                    {entry.name}
                  </span>
                  <span className="rounded-control-sm bg-glass-fill-heavy px-1.5 py-0.5 text-appkit-mini text-muted">
                    {entry.category}
                  </span>
                </div>
                <span className="line-clamp-2 text-appkit-mini text-muted">
                  {entry.description}
                </span>
              </div>
              <button
                type="button"
                onClick={() => void handleImport(entry)}
                disabled={importing === entry.slug}
                className="shrink-0 rounded-control bg-accent px-2 py-1 text-appkit-mini font-medium text-white transition-opacity disabled:opacity-40"
                data-testid={`skill-catalog-import-${entry.slug}`}
              >
                {importing === entry.slug ? t("adding") : t("add")}
              </button>
            </div>
          ),
        )}
      </div>
    </div>
  );
}
